// reenactMap.js
//
// Turns the backend's reenact sessions into a *segment map*: a sorted, contiguous
// list of spans over the narration-audio timeline, each saying what the video
// should be doing while that span plays. The narration audio is the master clock
// during a reenact — given any audio time T, lookupState(map, T) says whether the
// video should be playing and at what position, so scrubbing the narration snaps
// the video to the matching moment.
//
// A span's video state is one of:
//   { playing: false, position }            — hold the video paused at `position`
//                                             (position may be null: don't move it)
//   { playing: true, anchorPos, anchorAudio } — video is rolling; its position at
//                                             audio time T is anchorPos + (T - anchorAudio)

/**
 * Build the segment map.
 *
 * @param {Array} sessions - included sessions from GET /videos/{id}/reenact.
 * @param {number} audioDuration - the narration file's real duration (seconds);
 *   pass 0/NaN before it is known to skip clamping.
 * @param {{ onlySessionIndex?: number|null }} [opts] - when set, build only that
 *   session's slice (single-session mode).
 * @returns {Array<{audioStart:number, audioEnd:number, video:object}>}
 */
export function buildSegmentMap(sessions, audioDuration, opts = {}) {
  const onlySessionIndex = opts.onlySessionIndex ?? null;
  const chosen = onlySessionIndex == null
    ? sessions
    : sessions.filter((s) => s.index === onlySessionIndex);

  const segments = [];

  for (const session of chosen) {
    let state = {
      playing: !!session.initial_state.playing,
      position: session.initial_state.position,
    };
    let cursor = session.audio_offset;
    const sessionEnd = session.audio_offset + session.duration;

    const events = [...(session.events || [])].sort((a, b) => a.offset - b.offset);
    for (const event of events) {
      const tA = session.audio_offset + event.offset;
      if (tA > cursor) {
        segments.push(makeSegment(cursor, tA, state));
        cursor = tA;
      }
      state = applyEvent(state, event);
    }
    if (sessionEnd > cursor) {
      segments.push(makeSegment(cursor, sessionEnd, state));
    }
  }

  return clampToAudio(segments, audioDuration);
}

/**
 * One row per session for the jumper: where its narration sits in the file.
 *
 * A jump is navigation of the narration, so that slice is all a chip needs to
 * name. The stretch of *video* a session covers was shown too, and dropped: takes
 * routinely re-cover the same ground, so "vid 0:00–1:28" three times over told a
 * reviewer nothing. `start` disambiguates instead — it dates the take.
 *
 * @param {Array} sessions - included sessions from GET /videos/{id}/reenact.
 * @returns {Array<{index:number, start:string, audioStart:number,
 *   audioEnd:number, duration:number}>}
 */
export function summarizeSessions(sessions) {
  return (sessions || []).map((session) => ({
    index: session.index,
    start: session.start,
    audioStart: session.audio_offset,
    audioEnd: session.audio_offset + session.duration,
    duration: session.duration,
  }));
}

function applyEvent(state, event) {
  if (event.action === 'play') return { playing: true, position: event.position };
  if (event.action === 'pause') return { playing: false, position: event.position };
  if (event.action === 'seek') return { playing: state.playing, position: event.position };
  return state;
}

function makeSegment(audioStart, audioEnd, state) {
  // A "playing" span with no known position can't anchor — treat it as paused.
  if (state.playing && typeof state.position === 'number') {
    return {
      audioStart,
      audioEnd,
      video: { playing: true, anchorPos: state.position, anchorAudio: audioStart },
    };
  }
  return {
    audioStart,
    audioEnd,
    video: { playing: false, position: typeof state.position === 'number' ? state.position : null },
  };
}

// Reconcile the map with the audio file's real length. Recording-window durations
// only approximate the appended segment lengths (and a manual upload appends audio
// the log knows nothing about), so drop spans past the end, clamp the straddling
// one, and stretch the final span to cover any trailing audio.
function clampToAudio(segments, audioDuration) {
  if (!Number.isFinite(audioDuration) || audioDuration <= 0 || segments.length === 0) {
    return segments;
  }
  const kept = segments.filter((s) => s.audioStart < audioDuration);
  for (const s of kept) {
    s.audioEnd = Math.min(s.audioEnd, audioDuration);
  }
  if (kept.length > 0) {
    kept[kept.length - 1].audioEnd = audioDuration;
  }
  return kept;
}

/**
 * The video state the given narration-audio time maps to.
 * @returns {{playing: boolean, position: number|null}}
 */
export function lookupState(segments, T) {
  if (!segments || segments.length === 0) return { playing: false, position: null };
  if (T <= segments[0].audioStart) return stateAt(segments[0], segments[0].audioStart);
  for (const seg of segments) {
    if (T >= seg.audioStart && T < seg.audioEnd) return stateAt(seg, T);
  }
  const last = segments[segments.length - 1];
  return stateAt(last, Math.min(T, last.audioEnd));
}

function stateAt(seg, T) {
  if (seg.video.playing) {
    return { playing: true, position: seg.video.anchorPos + (T - seg.video.anchorAudio) };
  }
  return { playing: false, position: seg.video.position };
}
